import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { RefreshCw, Mail } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface RegenerateQRButtonProps {
  orderId: string;
  orderNumber: string;
}

export const RegenerateQRButton = ({ orderId, orderNumber }: RegenerateQRButtonProps) => {
  const [isRegenerating, setIsRegenerating] = useState(false);

  const handleRegenerateQR = async () => {
    setIsRegenerating(true);
    try {
      console.log('[RegenerateQR] Regenerating QR code for order:', orderId);

      const { data, error } = await supabase.functions.invoke('regenerate-qr-code', {
        body: { orderId }
      });

      if (error) {
        throw error;
      }

      console.log('[RegenerateQR] QR code regenerated:', data);

      toast({
        title: "QR Code régénéré",
        description: `Un nouvel email avec le QR code a été envoyé au client pour la commande ${orderNumber}.`,
      });
    } catch (error: any) {
      console.error('[RegenerateQR] Error:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible de régénérer le QR code",
        variant: "destructive"
      });
    } finally {
      setIsRegenerating(false);
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          disabled={isRegenerating}
        >
          {isRegenerating ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Régénération...
            </>
          ) : (
            <>
              <Mail className="h-4 w-4 mr-2" />
              Renvoyer le QR Code
            </>
          )}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Régénérer et renvoyer le QR Code</AlertDialogTitle>
          <AlertDialogDescription>
            Cette action va générer un nouveau QR code et code OTP pour cette commande et l'envoyer par email au client.
            <br /><br />
            <strong>Attention :</strong> L'ancien QR code ne sera plus valide.
            <br /><br />
            Voulez-vous continuer ?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Annuler</AlertDialogCancel>
          <AlertDialogAction onClick={handleRegenerateQR}>
            Régénérer et envoyer
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
